import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Spacing, Input, Button, Text } from '@reservamos/elements';
import SectionTitle from 'ui/atoms/SectionTitle';
import ExchangeInfoModal from 'ui/molecules/ExchangeInfoModal';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';

/**
 * SNForm Functional Component
 *
 * @param {Object} props - The component props.
 * @param {Function} props.handleSubmit - The function to be called when the form is submitted.
 * @param {string} props.ctaText - The text to be displayed on the call-to-action button.
 * @param {Function} props.handleClickButton - Function to execute when clicking the submit button.
 *
 * @returns {JSX.Element} The rendered form component.
 */
const SNForm = ({ handleSubmit, ctaText, handleClickButton }) => {
  const { t } = useTranslation('exchange');
  const features = useWhitelabelFeatures();
  const initialValues = {
    operationNumber: '',
    document: '',
    email: '',
  };

  const validationSchema = yup.object().shape({
    operationNumber: yup.string().required(''),
    document: yup.string().required(''),
    email: yup.string().required(''),
  });

  /**
   * The function `getSectionTitleContent` determines the title and description content based on the
   * values of `features.SHOW_EXCHANGE_TICKET_BUTTON` and `features.SHOW_CANCEL_TICKET_BUTTON`.
   * @returns The function `getSectionTitleContent` returns an object with a `title` and `description`
   * based on the conditions of the `features` object. The returned object will have a `title` and
   * `description` based on whether `SHOW_EXCHANGE_TICKET_BUTTON` and `SHOW_CANCEL_TICKET_BUTTON` are
   * true or false.
   */
  const getSectionTitleContent = () => {
    if (features.SHOW_EXCHANGE_TICKET_BUTTON && features.SHOW_CANCEL_TICKET_BUTTON) {
      return {
        title: t('title.exchange_cancelations'),
        description: t('subtitle.exchange_cancelations'),
      };
    }
    if (!features.SHOW_EXCHANGE_TICKET_BUTTON && features.SHOW_CANCEL_TICKET_BUTTON) {
      return {
        title: t('title.cancelations'),
        description: t('subtitle.cancelations'),
      };
    }
    if (features.SHOW_EXCHANGE_TICKET_BUTTON && !features.SHOW_CANCEL_TICKET_BUTTON) {
      return {
        title: t('title.exchange'),
        description: t('subtitle.exchange'),
      };
    }
    return {
      title: t('title.exchange_cancelations'),
      description: t('subtitle.exchange_cancelations'),
    };
  };

  /**
   * This function returns different disclaimer copy based on the values of the features
   * SHOW_EXCHANGE_TICKET_BUTTON and SHOW_CANCEL_TICKET_BUTTON.
   * @returns The `getDisclaimerFeatureCopy` function returns an object with `first` and `second`
   * properties, where the values of these properties are determined based on the values of the
   * `features.SHOW_EXCHANGE_TICKET_BUTTON` and `features.SHOW_CANCEL_TICKET_BUTTON` variables. The
   * specific text values for the `first` and `second` properties are retrieved using the `t` function
   * with
   */
  const getDisclaimerFeatureCopy = () => {
    if (features.SHOW_EXCHANGE_TICKET_BUTTON && features.SHOW_CANCEL_TICKET_BUTTON) {
      return {
        first: t('disclaimer.exchange_cancelations.first'),
        second: t('disclaimer.exchange_cancelations.second'),
      };
    }
    if (!features.SHOW_EXCHANGE_TICKET_BUTTON && features.SHOW_CANCEL_TICKET_BUTTON) {
      return {
        first: t('disclaimer.exchange_cancelations.first_cancelations'),
        second: t('disclaimer.exchange_cancelations.second_cancelations'),
      };
    }
    if (features.SHOW_EXCHANGE_TICKET_BUTTON && !features.SHOW_CANCEL_TICKET_BUTTON) {
      return {
        first: t('disclaimer.exchange_cancelations.first_exchange'),
        second: t('disclaimer.exchange_cancelations.second_exchange'),
      };
    }
    return {
      first: t('disclaimer.exchange_cancelations.first'),
      second: t('disclaimer.exchange_cancelations.second'),
    };
  };

  const sectionTitleContent = getSectionTitleContent();
  const disclaimerContent = getDisclaimerFeatureCopy();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) =>
        handleSubmit({
          operationNumbers: [values.operationNumber],
          document: values.document,
          email: values.email,
        })
      }
    >
      {({ values, handleChange, handleBlur, errors, touched }) => (
        <Form>
          <Spacing vertical responsiveSize="XL">
            <SectionTitle
              title={sectionTitleContent.title}
              description={sectionTitleContent.description}
            />
            <ul className="list-disc list-inside">
              <li className="text-base">{t('labels.ticket_number')}</li>
              <li className="text-base">{t('labels.passenger_document')}</li>
              <li className="text-base">{t('labels.email')}</li>
            </ul>
            <Spacing flexGrow isResponsive responsiveSize="S">
              <Input
                autocomplete="off"
                id="operationNumber"
                name="operationNumber"
                label={t('labels.ticket_number')}
                maxLength={15}
                errorMessage={errors.operationNumber}
                hasError={errors.operationNumber !== undefined && touched.operationNumber}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.operationNumber}
              />
              <Input
                id="document"
                name="document"
                label={t('labels.passenger_document')}
                maxLength={15}
                errorMessage={errors.document}
                hasError={errors.document !== undefined && touched.document}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.document}
              />
              <Input
                id="email"
                name="email"
                type="email"
                label={t('labels.email')}
                maxLength={60}
                errorMessage={errors.email}
                hasError={errors.email !== undefined && touched.email}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.email}
              />
            </Spacing>
            <ExchangeInfoModal />
            <Spacing justifyContent="space-between" isResponsive>
              <Spacing size="XS" vertical>
                {features.SHOW_EXCHANGE_DISCLAIMER && (
                  <>
                    <Text weight="bold">{disclaimerContent.first} </Text>
                    <Text weight="bold">{disclaimerContent.second}</Text>
                  </>
                )}
                {features.SHOW_EXCHANGE_TICKET_BUTTON && (
                  <Text weight="bold"> {t('disclaimer.exchange_cancelations.third')} </Text>
                )}
                {features.SHOW_REFUND_PENDING_DISCLAIMER && (
                  <Text weight="bold">{t('disclaimer.exchange_cancelations.refund_pending')}</Text>
                )}
              </Spacing>
              <Spacing isResponsive responsiveColumnReverse>
                <Button
                  buttonType="submit"
                  text={ctaText}
                  variant="accent"
                  isRounded
                  withHeight
                  fullWidthOnSmall
                  onClick={handleClickButton}
                />
              </Spacing>
            </Spacing>
          </Spacing>
        </Form>
      )}
    </Formik>
  );
};

const propTypes = {
  handleSubmit: PropTypes.func,
  ctaText: PropTypes.string,
  handleClickButton: PropTypes.func,
};

SNForm.propTypes = propTypes;

export default SNForm;
